/**
 * **The picker's result tiers, and the order inside them** (VALE-1423).
 *
 * Searching an aged-care provider's name used to return a tenth of the catalogue into
 * a 272px list of names differing by one word: `Respect Aged Care` came back as 229
 * rows, and the facility whose full name had been typed character-for-character ranked
 * 23rd. VALE-1420 fixed the ranking arithmetic; this module is what fixes the *shape*
 * of what comes back.
 *
 * **Two tiers, and the boundary between them is never crossed.**
 *
 * 1. **Contains** — the typed string appears in the row's own name as one unbroken run
 *    of characters, once case and punctuation are folded. A literal answer to what was
 *    asked. **Not gated by scope:** a literal match outranks whose row it is.
 * 2. **Close matches** — everything else the trigram predicate admitted, grouped by
 *    scope so the firm's own rows are never below the fold. `Strathaven` lands here,
 *    because containment cannot bridge it to `Strath-Haven`: the doubled `h` breaks the
 *    run. So does alias-only retrieval, which is how `MOT` still reaches Bell Direct.
 * 3. **Other institution types** — out-of-category close matches (VALE-1636). In a
 *    specific asset-type context the backend tags each fuzzy row's `typeScope`, and the
 *    rows whose type is not the one the asset implies (a bank asset + "respect" pulling in
 *    aged-care providers) are demoted here, below the in-category close matches. Empty
 *    outside an asset-type context, so a type-less search still renders as two tiers.
 *    Nothing is hidden — the row is demoted, not filtered, so a curated global is always
 *    reachable (VALE-1041 preserved); a `contains` row is never demoted (a literal match
 *    surfaces regardless of type).
 *
 * **Every tier is shown in full and nothing is hidden behind a count** (VALE-1423,
 * corrected at sign-off). An earlier shape capped each close-match group at five rows
 * and collapsed the rest to a plain "N more" line. That line was not selectable and
 * the rows behind it were never rendered, so the reader could not reach them by any
 * means short of retyping the query. The backend already bounds a search to
 * `SEARCH_LIMIT` (50) rows, the containment tier above absorbs the high-volume case,
 * and the list scrolls, so there is no unscannable list to protect against here, only
 * rows the reader asked for. The heading of each group carries its count so the reader
 * still knows how much is behind the scroll.
 *
 * **The sort re-orders within a group and never across them**, which is the single most
 * important line in this file. Sorting the whole result A–Z would reinstate VALE-1420's
 * bug in a new costume: the exact match returns to the middle of the list. That is why
 * the sort mode is an argument to a function that has already partitioned the rows,
 * rather than a comparator applied to the flat list.
 *
 * The tier itself is decided by the backend (`matchTier`), not here — the fold that
 * decides it is `institutionIdentityKey`, which is also what the create-time duplicate
 * guard runs on (VALE-1422), and a second copy of that regex in the frontend is
 * precisely how the duplicate class of bug returns.
 */

/**
 * How the reader has asked for the rows inside each group to be ordered.
 *
 * `best` is the default and must stay the default: making alphabetical the default
 * would penalise precision, which is the exact defect VALE-1420 just fixed. Someone who
 * types a facility's full name wants that facility, not the one whose name begins
 * with A.
 *
 * `alpha` has a real job all the same. Once containment has cut 229 rows to 23, all 23
 * contain what was typed, so their scores differ while carrying nothing a reader can
 * see or predict. At that point they are not searching, they are scanning a set, and
 * alphabetical is the only order that says where to look.
 *
 * `proximity` is the opt-in third mode (VALE-1573; generally available, VALE-1957). Unlike
 * `best` and `alpha`, which reorder a fetched page CLIENT-SIDE, proximity is BACKEND-ranked:
 * the search is re-issued with a `proximity` flag and the server distance-ranks the whole
 * catalogue from the firm's location, so a co-located institution reaches page 1 even
 * when a category query buried it at rank 68. The rows therefore arrive already ordered,
 * so within a group `proximity` preserves the backend order exactly as `best` does; only
 * `alpha` re-sorts. Offered to every firm as a per-user sort choice.
 */
export type InstitutionSortMode = 'best' | 'alpha' | 'proximity';

/**
 * Where the reader's sort choice is remembered (VALE-1423), shared by every mount of the
 * picker on purpose: the mount sites are all the same control doing the same job, and a
 * lawyer who chose a sort while filling in a bank account has not asked to be put back on
 * the default when they open an aged-care one a minute later. It lives here, beside the
 * sort mode it stores, so both the picker (which writes it) and the search fetch (which
 * reads it to decide whether to ask the backend to distance-rank) bind to one definition.
 */
export const INSTITUTION_SORT_STORAGE_KEY = 'vale-institution-picker-sort';

/**
 * The sort mode a freshly-mounted picker opens on (VALE-1596).
 *
 * `best` is the default and `alpha` is remembered across pickers, but `proximity`
 * (Nearest) is a PER-USE choice, never the remembered default. VALE-1588 made proximity a
 * standing sort — once picked it was restored on every later picker and so became the
 * de-facto default, which was never in scope. So a stored `proximity` is normalised back
 * to `best` here: the returned mode AND the stored key, because the fetch reads the same
 * key through `isProximitySortActive()` and would otherwise distance-rank the opening
 * listing behind a `Best match` label. Selecting Nearest still writes `proximity` and
 * takes effect for that interaction; it just does not persist as the default.
 *
 * The write is why this is not a pure reader and runs from the mount initialiser rather
 * than an effect: the initialiser is synchronous, so the key is corrected before the
 * open-time fetch can read it, with no effect-ordering race. Wrapped in try/catch because
 * a browser set to block site data throws on the accessor itself, which is not a reason to
 * fail to render a picker.
 */
export function initialInstitutionSortMode(): InstitutionSortMode {
  if (typeof window === 'undefined') return 'best';
  try {
    const stored = window.localStorage.getItem(INSTITUTION_SORT_STORAGE_KEY);
    if (stored === 'alpha') return 'alpha';
    if (stored === 'proximity') {
      // Clear the sticky choice so the open-time fetch does not browse by distance
      // while the toggle shows Best match.
      window.localStorage.setItem(INSTITUTION_SORT_STORAGE_KEY, 'best');
    }
    return 'best';
  } catch {
    return 'best';
  }
}

/**
 * Whether the reader has selected the proximity sort, read from the shared preference.
 *
 * Read by the search fetch to decide whether to ask the backend to distance-rank. It does
 * NOT consult the feature flag: the backend gates proximity on the flag per firm, so a
 * `proximity=true` sent while the flag is off is simply ignored (standard ranking). The
 * picker is what a firm without the flag never lets a reader select in the first place.
 */
export function isProximitySortActive(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    return (
      window.localStorage.getItem(INSTITUTION_SORT_STORAGE_KEY) === 'proximity'
    );
  } catch {
    return false;
  }
}

/** The fields this module reads. Deliberately structural, so tests need no fixtures. */
export interface TierableInstitution {
  id: number;
  name: string;
  /** Set by the backend on a search only; absent on a browsing listing. */
  matchTier?: 'contains' | 'fuzzy';
  /**
   * Whether this fuzzy row is in the category the asset implies (VALE-1636). Set by the
   * backend only on a Best-match search made in a specific asset-type context, so the
   * out-of-category fuzzy rows can be demoted into their own tier. Absent on a browse, a
   * proximity search, a type-less search, and on every `contains` row (a literal match
   * surfaces regardless of type).
   */
  typeScope?: 'in-category' | 'other-type';
  /** The backend's folded identity key for `name` — what `alpha` orders on. */
  sortName?: string;
  /** A row this firm added, as opposed to one from the Vale catalogue. */
  isCustom?: boolean;
}

export interface TieredInstitutionResults<T extends TierableInstitution> {
  /** Literal matches on the typed string. The answer to what was asked. */
  contains: T[];
  /** In-category close matches this firm added, in full. Shown first of the two close groups. */
  firmCloseMatches: T[];
  /** In-category close matches from the Vale catalogue, in full. */
  catalogueCloseMatches: T[];
  /**
   * Out-of-category close matches (VALE-1636) — fuzzy rows whose type is not the one the
   * asset implies (a bank asset + "respect" returning aged-care providers). Empty unless
   * the backend tagged rows with `typeScope`, i.e. a Best-match search in an asset-type
   * context. The picker renders these in a collapsed "Other institution types" tier below
   * the in-category close matches; nothing is hidden, only demoted (VALE-1041 preserved).
   */
  otherTypeMatches: T[];
  /** True when the backend tiered these rows at all, i.e. this was a search. */
  tiered: boolean;
}

/**
 * Order rows within one group.
 *
 * **`alpha` sorts on the folded name, not the raw one, and that is load-bearing.** Raw
 * ASCII puts `-` (0x2D) before every letter, so a raw sort files
 * `Respect Aged Care - Devonport TAS` under punctuation at the very top of the list
 * rather than under D where a reader would look for it — and, worse, pages away from
 * the unhyphenated twin it should be sitting beside.
 *
 * `sortName` is the backend's fold. The `name` fallback is for a row that predates the
 * field (or a test that omits it) and is deliberately lower-cased only: it is a
 * degraded order, never a second definition of the fold.
 */
function orderWithinGroup<T extends TierableInstitution>(
  rows: T[],
  sortMode: InstitutionSortMode
): T[] {
  if (sortMode !== 'alpha') {
    // `best` is the backend's own ordering, which the rows already arrive in.
    return rows;
  }
  return [...rows].sort((a, b) =>
    (a.sortName ?? a.name.toLowerCase()).localeCompare(
      b.sortName ?? b.name.toLowerCase()
    )
  );
}

/**
 * Partition one page of search results into the tiers the picker renders, ordering
 * inside each. Every group is returned in full (see the file header on why nothing is
 * collapsed behind a count).
 *
 * Returns `tiered: false` when the rows carry no `matchTier` — a browsing listing, or a
 * response from before this shipped — and in that case every row is left in `contains`
 * so the caller renders one plain ungrouped list exactly as it always did.
 */
export function tierInstitutionResults<T extends TierableInstitution>(
  institutions: T[],
  sortMode: InstitutionSortMode
): TieredInstitutionResults<T> {
  // Proximity is a flat, distance-ordered list (VALE-1610). The backend returns proximity
  // results ordered primarily by exact distance (nearest first); grouping them into the
  // contains/close-match tiers would reset that ordering at each tier boundary, so a far
  // containment match would jump above a nearer fuzzy one. Proximity is therefore never
  // tiered, whatever `matchTier` the rows carry: the whole point of the mode is one
  // nearest-first list.
  // Proximity is a flat, distance-ordered list (VALE-1610). The backend returns proximity
  // results ordered primarily by exact distance (nearest first); grouping them into the
  // contains/close-match tiers would reset that ordering at each tier boundary, so a far
  // containment match would jump above a nearer fuzzy one. Proximity is therefore never
  // tiered, whatever `matchTier` the rows carry: the whole point of the mode is one
  // nearest-first list.
  const tiered =
    sortMode !== 'proximity' &&
    institutions.some((institution) => institution.matchTier);

  if (!tiered) {
    return {
      contains: institutions,
      firmCloseMatches: [],
      catalogueCloseMatches: [],
      otherTypeMatches: [],
      tiered: false
    };
  }

  const contains = institutions.filter(
    (institution) => institution.matchTier === 'contains'
  );
  const closeMatches = institutions.filter(
    (institution) => institution.matchTier !== 'contains'
  );
  // Out-of-category fuzzy rows are demoted into their own tier (VALE-1636). The tag is
  // set by the backend only in an asset-type context; absent, every close match reads as
  // in-category and this group is empty, so a type-less search renders exactly as before.
  // A `contains` row is never tagged, so it stays in the top tier regardless of type
  // (VALE-1041's protected case).
  const otherType = closeMatches.filter(
    (institution) => institution.typeScope === 'other-type'
  );
  const inCategory = closeMatches.filter(
    (institution) => institution.typeScope !== 'other-type'
  );
  const firm = inCategory.filter((institution) => institution.isCustom);
  const catalogue = inCategory.filter((institution) => !institution.isCustom);

  return {
    contains: orderWithinGroup(contains, sortMode),
    firmCloseMatches: orderWithinGroup(firm, sortMode),
    catalogueCloseMatches: orderWithinGroup(catalogue, sortMode),
    otherTypeMatches: orderWithinGroup(otherType, sortMode),
    tiered: true
  };
}
